import { Injectable, signal, TemplateRef } from '@angular/core';
import { TreeNode, TreeNodeEvent } from '@libs/front/components/tree-view/tree-view.component';

export interface NavLeftConfig {
  treeData:          TreeNode[];
  searchText?:       string;
  selectedNodeId?:   string | null;
  labelEllipsis?:    'start' | 'end';   // position des « ... » sur les noms (défaut : 'end')
  showCheckbox?:     boolean;   // false = menu de sélection simple (ex : config)
  showSelectAll?:    boolean;   // false = pas d'en-tête « Select all » (ex : config)
  showGuides?:       boolean;   // false = pas de traits de séparation (ex : config)
  persistKey?:       string;    // clé localStorage isolée pour la sélection
  width?:            string;    // largeur du nav-left (défaut 22.5rem) — ex : '16rem' en config
  selectParent?:     boolean;
  showExpandCollapse?: boolean;         // bouton plier/déplier tout dans l'en-tête du tree
  collapsible?:      boolean;   // true = flèche pour replier le nav-left en rail vertical (ex : site)
  collapsedLabel?:   string;    // texte écrit verticalement dans le rail replié (ex : nom du blast)
  // ── Templates layout nav-left ─────────────────────────────────────────────
  headerTemplate?:   TemplateRef<any> | null;   // bouton retour
  searchTemplate?:   TemplateRef<any> | null;   // search + actions toolbar
  siteCardTemplate?: TemplateRef<any> | null;   // card verte nom du site
  settingsTemplate?: TemplateRef<any> | null;   // onglet Settings (grouping)
  actionsTemplate?:  TemplateRef<any> | null;   // boutons extra (mode area)
  // ── Callbacks ─────────────────────────────────────────────────────────────
  onNodeClick?:      (event: TreeNodeEvent) => void;
  onBadgeClick?:     (event: TreeNodeEvent) => void;
  onCheckboxChange?: (event: TreeNodeEvent) => void;
  onSelectAll?:      (checked: boolean) => void;
}

const LS_COLLAPSED = 'navLeft.collapsed';

@Injectable({ providedIn: 'root' })
export class NavLeftService {

  private _config = signal<NavLeftConfig>({ treeData: [] });
  private _mobileNavVisible = signal(true);
  private _collapsed = signal<boolean>(lsLoadCollapsed());

  readonly config           = this._config.asReadonly();
  readonly mobileNavVisible = this._mobileNavVisible.asReadonly();
  /** Nav-left replié en rail vertical (mémorisé entre les sessions). N'a d'effet que si `collapsible`. */
  readonly collapsed        = this._collapsed.asReadonly();

  showMobileNav(): void { this._mobileNavVisible.set(true); }
  hideMobileNav(): void { this._mobileNavVisible.set(false); }

  setCollapsed(collapsed: boolean): void {
    this._collapsed.set(collapsed);
    try { localStorage.setItem(LS_COLLAPSED, String(collapsed)); } catch {}
  }

  toggleCollapsed(): void { this.setCollapsed(!this._collapsed()); }

  setCollapsedLabel(collapsedLabel: string): void {
    this._config.update(c => ({ ...c, collapsedLabel }));
  }

  set(config: NavLeftConfig): void {
    this._config.set(config);
  }

  setTreeData(treeData: TreeNode[]): void {
    this._config.update(c => ({ ...c, treeData }));
  }

  setSearchText(searchText: string): void {
    this._config.update(c => ({ ...c, searchText }));
  }

  setSelectedNodeId(id: string): void {
    this._config.update(c => ({ ...c, selectedNodeId: id }));
  }

  reset(): void {
    this._config.set({ treeData: [] });
  }
}

function lsLoadCollapsed(): boolean {
  try { return localStorage.getItem(LS_COLLAPSED) === 'true'; } catch { return false; }
}
